import { assertNever } from "../lib/assertNever";
import { isJust, type Maybe } from "../lib/maybe";
import type { ZwChapterHeadingStyle } from "../lib/zwConfig";
import { defaultZwChapterHeadingStyle } from "../lib/zwConfig";

/** Default chapter-title size in PDF / EPUB (matches generic `h1` before this style). */
export const CHAPTER_TITLE_BASE_PT = 19;

/** Default chapter-title size in DOCX half-points (16pt). */
export const CHAPTER_TITLE_BASE_HALF_POINTS = 32;

/** Body font size used as the em reference when converting spacing to twips. */
export const CHAPTER_TITLE_EM_REF_PT = 12;

const TWIPS_PER_POINT = 20;

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function resolveChapterHeadingStyle(
  style: Maybe<ZwChapterHeadingStyle>,
): ZwChapterHeadingStyle {
  return isJust(style) ? style.value : defaultZwChapterHeadingStyle();
}

/** CSS `text-align` for the injected chapter title. */
export function chapterTitleTextAlign(
  alignment: ZwChapterHeadingStyle["alignment"],
): "left" | "center" | "right" {
  switch (alignment) {
    case "left":
      return "left";
    case "center":
      return "center";
    case "right":
      return "right";
    default:
      return assertNever(alignment);
  }
}

export function chapterTitleFontSizePt(scale: number): number {
  return CHAPTER_TITLE_BASE_PT * scale;
}

export function chapterTitleDocxHalfPoints(scale: number): number {
  return Math.round(CHAPTER_TITLE_BASE_HALF_POINTS * scale);
}

/** Convert an em spacing value to DOCX twips (body 12pt em). */
export function chapterTitleSpacingTwips(em: number): number {
  return Math.round(em * CHAPTER_TITLE_EM_REF_PT * TWIPS_PER_POINT);
}

/**
 * Markup for the injected chapter title block (image + h1). Image `src` is the
 * project-relative path; PDF/EPUB rewrite it to a data URL / package path later.
 */
export function chapterTitleHtml(
  name: string,
  style: ZwChapterHeadingStyle,
): string {
  const title = `<h1 class="chapter-title">${name.split("\n").map(escapeHtml).join("<br>")}</h1>`;
  const imagePath = style.image.trim();
  if (imagePath.length === 0) {
    return title;
  }
  const image = `<img class="chapter-title-image" src="${escapeHtml(imagePath)}" alt="" />`;
  return style.imagePosition === "below"
    ? `${title}\n${image}`
    : `${image}\n${title}`;
}

/**
 * CSS for `.chapter-title` / `.chapter-title-image` in PDF and EPUB books.
 * Scoped so scene `h1`s keep the generic heading rules.
 */
function chapterTitleImageMargin(align: "left" | "center" | "right"): string {
  switch (align) {
    case "left":
      return "0 auto 0 0";
    case "right":
      return "0 0 0 auto";
    case "center":
      return "0 auto";
    default:
      return assertNever(align);
  }
}

export function chapterTitleCss(style: ZwChapterHeadingStyle): string {
  const align = chapterTitleTextAlign(style.alignment);
  const sizePt = chapterTitleFontSizePt(style.scale);
  const imageMargin = chapterTitleImageMargin(align);
  const gap = Math.max(style.spaceAfter * 0.5, 0.35);
  return `.chapter-title {
  text-align: ${align};
  font-size: ${sizePt}pt;
  margin: ${style.spaceBefore}em 0 ${style.spaceAfter}em;
}

.chapter-title-image {
  display: block;
  max-width: 40%;
  max-height: 2.5in;
  height: auto;
  margin: ${imageMargin};
  margin-bottom: ${gap}em;
}

.chapter-title-image + .chapter-title {
  margin-top: 0;
}

.chapter-title + .chapter-title-image {
  margin-top: 0;
  margin-bottom: ${style.spaceAfter}em;
}
`;
}
